/**
 * DateTimePickerButton Component
 * 
 * A reusable button component for date and time selection with consistent styling.
 * Originally created for Events, now used across Events and Bulletins for scheduled publishing.
 * 
 * Features:
 * - Displays formatted date and time
 * - Calendar icon for visual clarity
 * - Error state styling and message display
 * - Consistent button appearance across the app
 * - Accessible touch target with proper hit slop
 * - Automatic date/time formatting
 * 
 * Usage:
 * ```tsx
 * <DateTimePickerButton
 *   label="Publish Date"
 *   value={publishDate}
 *   onPress={() => setShowDatePicker(true)}
 *   error={errors.publishTime}
 * />
 * ```
 */
import React, { memo, useCallback, useRef } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform } from 'react-native';
import type { View as RNView } from 'react-native';
import { ADMIN_COLORS } from '@/constants/admin';
import { COLORS } from '@/constants/residents';
import { SVG_COMPONENTS } from '@/assets/admin';
import { AnchorRect } from '@/hooks/common/useAnchoredPosition';

export interface DateTimePickerButtonProps {
  label: string;
  value: Date;
  onPress: (anchor?: AnchorRect) => void;
  error?: string;
  placeholder?: string;
}

export const DateTimePickerButton = memo<DateTimePickerButtonProps>(({
  label,
  value,
  onPress,
  error,
  placeholder = 'Select date and time',
}) => {
  const buttonRef = useRef<RNView | null>(null);

  const formatDateTime = (date: Date) => {
    return `${date.toLocaleDateString()} at ${date.toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    })}`;
  };

  const handlePress = useCallback(() => {
    if (Platform.OS === 'web' && buttonRef.current && (buttonRef.current as any)?.measureInWindow) {
      (buttonRef.current as any).measureInWindow((x: number, y: number, w: number, h: number) => {
        onPress({ x, y, w, h });
      });
      return;
    }
    onPress();
  }, [onPress]);

  return (
    <View style={styles.inputGroup}>
      <Text style={styles.label}>{label}</Text>
      <TouchableOpacity 
        ref={buttonRef}
        style={[styles.dateTimeButton, error && styles.inputError]} 
        onPress={handlePress}
      >
        <Text style={[
          styles.dateTimeText,
          !value && styles.placeholderText
        ]}>
          {value ? formatDateTime(value) : placeholder}
        </Text>
        <SVG_COMPONENTS.CHEVRON_DOWN width={16} height={16} color={COLORS.TEXT_WHITE} />
      </TouchableOpacity>
      {error && <Text style={styles.errorText}>{error}</Text>}
    </View>
  );
});

DateTimePickerButton.displayName = 'DateTimePickerButton';

const styles = StyleSheet.create({
  inputGroup: {
    marginBottom: 16,
  },
  label: {
    fontSize: 14,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    marginBottom: 8,
  },
  dateTimeButton: {
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    paddingHorizontal: 12,
    paddingVertical: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    minHeight: 48,
  },
  dateTimeText: {
    fontSize: 16,
    color: ADMIN_COLORS.TEXT_PRIMARY,
  },
  placeholderText: {
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
  inputError: {
    borderColor: ADMIN_COLORS.ERROR,
  },
  errorText: {
    fontSize: 12,
    color: ADMIN_COLORS.ERROR,
    marginTop: 4,
  },
});
